import { useState, useEffect, useCallback } from 'react';
import type { ComboBoxOption } from '@ui/ComboBox';
import type { RideType, UserBike, Vehicle, Make } from 'models/shared';
import { client } from '@util/getTypesense';
import useDebounce from '@util/hooks/useDebounce';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CheckIcon, HelpCircleIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import HelpWithVehicleModal from './HelpWithVehicleModal';
import VehicleOptionSkeleton from './VehicleOptionSkeleton';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import { usePathname, useRouter } from 'next/navigation';
import { logError } from '@util/logError';
import { normalize } from '@util/index';

type SelectionStep = 'search' | 'selectYear';

type PartialVehicleData = {
  make: string;
  model: string;
  type: Record<Vehicle['type'], RideType[]>;
};

type YearOption = {
  year: number;
  id: string;
  label: string;
};

type SearchByVehicleFormProps = {
  onBikeComplete: (bike: UserBike) => void;
  onDismiss: () => void;
  className?: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
};

export default function SearchByVehicleForm({
  onBikeComplete,
  onDismiss,
  className = '',
  searchQuery,
  setSearchQuery,
}: SearchByVehicleFormProps) {
  const [step, setStep] = useState<SelectionStep>('search');
  const [vehicleOptions, setVehicleOptions] = useState<
    (ComboBoxOption & { hit: any })[]
  >([]);
  const [yearOptions, setYearOptions] = useState<YearOption[]>([]);
  const [helpWithVehicleModalOpen, setHelpWithVehicleModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [extractedYear, setExtractedYear] = useState<number | null>(null);
  const { userDoc } = useAuth();
  const { selectedBike, setSelectedBike } = useBikeFilter();
  const router = useRouter();
  const pathname = usePathname();

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const isVehicleSelected = (vehicleData: PartialVehicleData) => {
    return (
      vehicleData.make === selectedBike.make &&
      vehicleData.model === selectedBike.model
    );
  };

  const isYearSelected = (year: number) => {
    return year === selectedBike.year;
  };

  const searchVehicles = useCallback(async (q: string) => {
    if (!q?.trim()) {
      setVehicleOptions([]);
      setIsLoading(false);
      setExtractedYear(null);
      return;
    }

    setIsLoading(true);

    try {
      let searchQueryText = q.trim();

      let extractedYear: number | null = null;

      const yearIndex = searchQueryText.search(/\d{4}/g);

      if (yearIndex !== -1) {
        const yearMatch = searchQueryText.match(/\d{4}/g);

        if (yearMatch) {
          const year = parseInt(yearMatch[0]);

          if (year >= 1950 && year < 2100) {
            extractedYear = year;

            if (yearIndex === 0) {
              searchQueryText = searchQueryText.substring(4).trim();
            } else {
              searchQueryText = searchQueryText.replace(/\d{4}/, '').trim();
            }
          }
        }
      }

      setExtractedYear(extractedYear);

      const normalizedSearchQueryText = normalize(searchQueryText);

      const searchParams: Record<string, any> = {
        q: normalizedSearchQueryText,
        query_by: 'normalized_name,make,model',
        per_page: 25,
      };

      // Add year filter if extracted from search query
      if (extractedYear) {
        searchParams.filter_by = `years:${extractedYear}`;
      }

      const res = await client
        .collections('vehicles_alias')
        .documents()
        .search(searchParams);

      const uniqueVehicles = new Map<
        string,
        {
          make: string;
          model: string;
          type: Record<Vehicle['type'], RideType[]>;
          hit: any;
        }
      >();

      const parsedType: Record<Vehicle['type'], RideType[]> = {
        DIRT_BIKE: ['Dirt Bikes'],
        ATV: ['ATV'],
        UTV: ['UTV'],
        STREET: ['Street'],
        TRIALS: ['Dirt Bikes'],
        DUAL_SPORT: ['Street', 'Dual-Sport'],
        SNOW: ['Snow'],
        WATER: [],
      };

      // adding hit documents to unique vehicles map
      (res.hits || []).forEach((hit: any) => {
        const vehicle = hit.document;
        const key = `${vehicle.make}-${vehicle.model}`;

        if (!uniqueVehicles.has(key)) {
          uniqueVehicles.set(key, {
            make: vehicle.make,
            model: vehicle.model,
            type: parsedType,
            hit,
          });
        }
      });

      const options: (ComboBoxOption & { hit: any })[] = Array.from(
        uniqueVehicles.entries()
      ).map(([key, vehicleData]) => {
        const label = extractedYear
          ? `${extractedYear} ${vehicleData.make} ${vehicleData.model}`.trim()
          : `${vehicleData.make} ${vehicleData.model}`.trim();

        return {
          id: key,
          label,
          value: JSON.stringify({
            ...vehicleData,
            displayYear: extractedYear,
          }),
          hit: vehicleData.hit,
        };
      });

      setVehicleOptions(options);
    } catch (error) {
      logError(error, 'Error searching vehicles:');
      setVehicleOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery && step === 'search') {
      searchVehicles(debouncedSearchQuery);
    } else {
      setVehicleOptions([]);
      setIsLoading(false);
    }
  }, [debouncedSearchQuery, step, searchVehicles]);

  const handleVehicleSelect = async (option: {
    hit: any;
    id: string;
    label: string;
    value: string;
  }) => {
    try {
      const vehicleData: PartialVehicleData = JSON.parse(option.value);

      setSelectedBike({
        ...selectedBike,
        make: vehicleData.make as Make,
        model: vehicleData.model,
        year: null,
        allYears: option.hit.document.years,
      });

      const yearOptions = option.hit.document.years.map((year: number) => ({
        year,
        id: `${vehicleData.make}-${vehicleData.model}-${year}`,
        label: `${year}`,
      }));

      setYearOptions(yearOptions);

      if (extractedYear) {
        const res = await client
          .collections('vehicles_alias')
          .documents()
          .search({
            q: `${vehicleData.make} ${vehicleData.model}`,
            query_by: 'make,model',
            filter_by: `make:=${vehicleData.make} && model:=${vehicleData.model}`,
            per_page: 100,
          });

        const availableYears = new Set<number>();
        (res.hits || []).forEach((hit: any) => {
          const vehicle = hit.document;
          if (vehicle.years) {
            vehicle.years.forEach((year: number) => {
              availableYears.add(year);
            });
          }
        });

        if (availableYears.has(extractedYear)) {
          const completeVehicle: UserBike = {
            make: vehicleData.make as Make,
            model: vehicleData.model,
            year: extractedYear,
            date_added: null,
            downloadUrl: '',
            imageUrl: '',
            in_feed: false,
            type: 'Dirt Bikes' as RideType,
          };

          setSelectedBike({
            ...completeVehicle,
            allYears: option.hit.document.years,
          });

          onBikeComplete(completeVehicle);
          onDismiss?.();
          return;
        }
      }

      if (!extractedYear) {
        setStep('selectYear');
      }
    } catch (error) {
      logError(error, 'Error parsing vehicle data:');
      setStep('selectYear');
    }
  };

  const handleYearSelect = (yearOption: YearOption) => {
    if (!selectedBike) return;

    const completeVehicle = {
      make: selectedBike.make as Make,
      model: selectedBike.model ?? '',
      year: yearOption.year,
      date_added: null,
      downloadUrl: '',
      imageUrl: '',
      in_feed: false,
      type: 'Dirt Bikes' as RideType,
      allYears: yearOptions.map((y) => y.year),
    };

    setSelectedBike(completeVehicle);
    onBikeComplete(completeVehicle);
    onDismiss?.();
  };

  const handleBackToSearch = () => {
    setStep('search');
    setYearOptions([]);
  };

  const sortedVehicleOptions = [...vehicleOptions].sort((a, b) => {
    const vehicleA: PartialVehicleData = JSON.parse(a.value);
    const vehicleB: PartialVehicleData = JSON.parse(b.value);

    const isSelectedA = isVehicleSelected(vehicleA);
    const isSelectedB = isVehicleSelected(vehicleB);

    if (isSelectedA && !isSelectedB) return -1;
    if (!isSelectedA && isSelectedB) return 1;
    return 0;
  });

  const sortedYearOptions = [...yearOptions].sort((a, b) => {
    const isSelectedA = isYearSelected(a.year);
    const isSelectedB = isYearSelected(b.year);

    if (isSelectedA && !isSelectedB) return -1;
    if (!isSelectedA && isSelectedB) return 1;
    return b.year - a.year;
  });

  const handleTalkToSupport = async () => {
    if (!userDoc) {
      router.push(`/login?redirect=${encodeURIComponent(pathname ?? '')}`);
      return;
    }

    setHelpWithVehicleModalOpen(true);
  };

  if (step === 'selectYear') {
    return (
      <div className={`flex flex-col gap-y-6 overflow-y-hidden ${className}`}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleBackToSearch}
            className="flex items-center gap-x-2 text-[1.6rem] font-medium text-zinc-600 hover:text-zinc-800"
          >
            <ArrowLeftIcon className="h-6 w-6" />
            Select other vehicle
          </button>
        </div>

        {selectedBike && (
          <div className="rounded-[1.5rem] bg-zinc-50 p-4">
            <p className="text-[1.6rem] text-zinc-600">
              Selected:{' '}
              <span className="font-semibold">
                {selectedBike.make} {selectedBike.model}
              </span>
            </p>
          </div>
        )}

        {yearOptions.length === 0 && (
          <div className="flex h-[30rem] w-full items-center justify-center">
            <div className="flex flex-col items-center gap-y-6">
              <span className="text-[1.8rem] font-medium text-zinc-400">
                No years found for this vehicle.
              </span>
              <button
                type="button"
                className="flex h-[5.5rem] items-center gap-x-2 rounded-[1.5rem] bg-brand-secondary px-[2rem] py-[1rem] text-[1.8rem] font-semibold text-white"
                onClick={handleTalkToSupport}
              >
                <HelpCircleIcon className="h-8 w-8 text-white" />
                Talk to Support
              </button>
            </div>
          </div>
        )}

        {yearOptions.length > 0 && (
          <div className="flex max-h-[45rem] w-full flex-col gap-y-6 overflow-y-scroll pb-8 sm:max-h-[50rem] sm:pb-48">
            {sortedYearOptions.map((yearOption) => {
              const isSelected = isYearSelected(yearOption.year);

              return (
                <button
                  type="button"
                  key={yearOption.id}
                  className={`duration-50 flex h-[6rem] w-full cursor-pointer items-center justify-between rounded-[1.5rem] px-[1.5rem] py-[1rem] text-left text-[1.8rem] font-medium text-zinc-800 transition-all ${
                    isSelected ? 'bg-red-50' : 'hover:bg-zinc-50 '
                  }`}
                  onClick={() => handleYearSelect(yearOption)}
                >
                  <span className="w-[30rem] capitalize leading-relaxed">
                    {yearOption.label}
                  </span>

                  {isSelected && (
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-secondary p-[0.5rem]">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <HelpWithVehicleModal
          isOpen={helpWithVehicleModalOpen}
          dismiss={() => setHelpWithVehicleModalOpen(false)}
          onCloseAllModals={() => {
            onDismiss();
            setHelpWithVehicleModalOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-y-6 overflow-y-hidden ${className}`}>
      <div className="flex h-[5.5rem] items-center justify-between gap-x-8 overflow-y-hidden rounded-[1.5rem] border border-zinc-300 bg-zinc-100 px-[2rem] py-[1rem]">
        <input
          type="text"
          placeholder="2024 Yamaha YZ250F"
          className="h-full w-full border-none bg-transparent text-[1.8rem] text-zinc-700 placeholder:text-zinc-400"
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          value={searchQuery}
        />

        <div className="flex items-center gap-x-2">
          {searchQuery === '' ? (
            <MagnifyingGlassIcon className="h-10 w-10 text-zinc-500" />
          ) : (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
              className="cursor-pointerF"
            >
              <XIcon className="h-10 w-10 text-zinc-500" />
            </button>
          )}
        </div>
      </div>

      {isLoading && searchQuery !== '' && <VehicleOptionSkeleton count={5} />}

      {!isLoading && searchQuery === '' && (
        <div className="flex h-[30rem] w-full items-center justify-center">
          <div className="flex flex-col items-center gap-y-6">
            <MagnifyingGlassIcon className="h-32 w-32 text-zinc-300 sm:h-24 sm:w-24" />

            <span className="text-[1.6rem] font-semibold text-zinc-400">
              Start typing to find your vehicle.
            </span>
          </div>
        </div>
      )}

      {!isLoading && searchQuery !== '' && !vehicleOptions.length && (
        <div className="flex h-[30rem] w-full items-center justify-center">
          <div className="flex flex-col items-center gap-y-6">
            <div className="flex flex-col items-center gap-y-2">
              <span className="text-[1.8rem] font-medium text-zinc-400">
                No results.
              </span>
              <span className="text-[1.8rem] font-medium text-zinc-400">
                Try another make or model
              </span>
            </div>

            <button
              type="button"
              className="flex h-[5.5rem] items-center gap-x-2 rounded-[1.5rem] bg-brand-secondary px-[2rem] py-[1rem] text-[1.8rem] font-semibold text-white"
              onClick={handleTalkToSupport}
            >
              <HelpCircleIcon className="h-8 w-8 text-white" />
              Talk to Support
            </button>
          </div>
        </div>
      )}

      {!isLoading && !!vehicleOptions.length && searchQuery !== '' && (
        <div className="flex max-h-[45rem] w-full flex-col gap-y-6 overflow-y-scroll pb-8 sm:max-h-[50rem] sm:pb-48">
          {sortedVehicleOptions.map((vehicle) => {
            const vehicleData: PartialVehicleData = JSON.parse(vehicle.value);
            const isSelected = isVehicleSelected(vehicleData);
            const hasSelectedYear = isSelected && selectedBike.year;

            return (
              <div key={vehicle.id} className="flex flex-col gap-y-2">
                <button
                  type="button"
                  className={`duration-50 flex h-[6rem] w-full cursor-pointer items-center justify-between rounded-[1.5rem] px-[1.5rem] py-[1rem] text-left text-[1.8rem] font-medium text-zinc-800 transition-all ${
                    isSelected ? 'bg-red-50' : 'hover:bg-zinc-50 '
                  }`}
                  onClick={() => handleVehicleSelect(vehicle)}
                >
                  <span className="w-[30rem] capitalize leading-relaxed [&_mark]:bg-transparent [&_mark]:font-semibold">
                    {extractedYear && (
                      <span className="font-semibold">{extractedYear} </span>
                    )}
                    {vehicle.hit.highlight?.make?.snippet ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: vehicle.hit.highlight.make.snippet,
                        }}
                      />
                    ) : (
                      <span>{vehicle.hit.document.make}</span>
                    )}
                    <span> </span>
                    {vehicle.hit.highlight?.model?.snippet ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: vehicle.hit.highlight.model.snippet,
                        }}
                      />
                    ) : (
                      <span>{vehicle.hit.document.model}</span>
                    )}
                  </span>

                  {isSelected && (
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-secondary p-[0.5rem]">
                      <CheckIcon className="h-full w-full text-white" />
                    </div>
                  )}
                </button>

                {hasSelectedYear && (
                  <div className="flex items-center justify-between rounded-[1.5rem] bg-zinc-100 px-4 py-6">
                    <div className="flex items-center gap-x-3">
                      <span className="text-[1.6rem] text-zinc-600">
                        <span className="font-semibold">
                          {selectedBike.year}
                        </span>{' '}
                        selected
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStep('selectYear');

                        console.log({ selectedBike });

                        setYearOptions(
                          selectedBike.allYears?.map((year) => ({
                            year,
                            id: `${vehicleData.make}-${vehicleData.model}-${year}`,
                            label: `${year}`,
                          })) ?? []
                        );
                      }}
                      className="text-[1.4rem] font-medium text-brand-secondary underline hover:text-brand-secondary/80 sm:text-[1.6rem]"
                    >
                      Edit year
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className="duration-50 mt-6 w-full cursor-pointer rounded-[1.5rem] border border-zinc-200 bg-zinc-50 p-6 text-[1.8rem] text-zinc-600 transition-colors hover:bg-zinc-100"
            onClick={handleTalkToSupport}
          >
            Didn&apos;t find your vehicle?{' '}
            <span className="font-medium text-brand-secondary">
              Talk to support.
            </span>
          </button>
        </div>
      )}

      <HelpWithVehicleModal
        isOpen={helpWithVehicleModalOpen}
        dismiss={() => setHelpWithVehicleModalOpen(false)}
        onCloseAllModals={() => {
          onDismiss();
          setHelpWithVehicleModalOpen(false);
        }}
      />
    </div>
  );
}
